import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { 
  login as apiLogin, 
  logout as apiLogout,
  isAuthenticated as checkIsAuthenticated,
  getAccessToken,
  clearAuthData,
  getUserId,
  signInWithGoogle as apiSignInWithGoogle,
  AuthResponse
} from '../services/authService';
import api from '../services/api';
import { authChannel } from '../utils/authChannel';

// Signal for when auth state changes - can be used by other contexts
export const authStateChangeSignal = new EventTarget();

// Verification tracker to prevent duplicate calls
let lastVerificationTime = 0;
const VERIFICATION_COOLDOWN = 2000; // 2 seconds
let pendingVerifyPromise = null;

interface User {
  userId: string;
  email: string;
  username: string;
  profilePictureUrl?: string;
  profileImageUrl?: string;
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<AuthResponse>;
  logout: () => Promise<void>;
  signInWithGoogle: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const verifyingRef = useRef(false);
  
  // Check authentication status and load user data with debounce
  const loadUserData = useCallback(async () => {
    // Skip if we're already verifying
    if (verifyingRef.current) {
      console.log('Auth verification already in progress, using existing promise');
      return pendingVerifyPromise;
    }
    
    // Skip if we've verified recently
    const now = Date.now();
    if (now - lastVerificationTime < VERIFICATION_COOLDOWN) {
      console.log('Auth verification on cooldown, skipping');
      return;
    }
    
    verifyingRef.current = true;
    lastVerificationTime = now;
    setLoading(true); // Set loading state when starting verification
    
    // Create a new verification promise
    pendingVerifyPromise = (async () => {
      try {
        if (checkIsAuthenticated()) {
          try {
            console.log('Verifying auth status with server at:', new Date().toISOString());
            const response = await api.get('/auth/verify', {
              withCredentials: true
            });
            
            if (response.data) {
              setUser({
                userId: response.data.userId,
                email: response.data.email,
                username: response.data.username,
                profilePictureUrl: response.data.profilePictureUrl,
                profileImageUrl: response.data.profileImageUrl
              });
              setIsAuthenticated(true);
              return true;
            } else {
              setUser(null);
              setIsAuthenticated(false);
              return false;
            }
          } catch (error) {
            console.error('Auth verification failed:', error);
            setUser(null);
            setIsAuthenticated(false);
            return false;
          }
        } else {
          setUser(null);
          setIsAuthenticated(false);
          return false;
        }
      } catch (error) {
        console.error('Error checking auth state:', error);
        setUser(null);
        setIsAuthenticated(false);
        return false;
      } finally {
        setLoading(false);
        verifyingRef.current = false;
      }
    })();
    
    return pendingVerifyPromise;
  }, []);
  
  // Initial auth check on mount
  useEffect(() => {
    loadUserData();
    
    // Listen for token changes
    const handleTokenChange = () => {
      loadUserData();
    };
    
    // Listen for auth expiration
    const handleAuthExpired = () => {
      setUser(null);
      setIsAuthenticated(false);
    };
    
    window.addEventListener('tokenChange', handleTokenChange);
    window.addEventListener('authExpired', handleAuthExpired);
    
    return () => {
      window.removeEventListener('tokenChange', handleTokenChange);
      window.removeEventListener('authExpired', handleAuthExpired);
    };
  }, [loadUserData]);
  
  // Emit an event when authentication state changes
  useEffect(() => {
    // Dispatch event for other contexts to listen to
    authStateChangeSignal.dispatchEvent(new CustomEvent('authStateChange', {
      detail: { isAuthenticated }
    }));
  }, [isAuthenticated]);
  
  // Login handler
  const login = async (email: string, password: string) => {
    setLoading(true);
    try {
      const response = await apiLogin({ email, password });
      
      if (response && response.userId) {
        // Immediately set authenticated state without waiting for verification
        setUser({
          userId: response.userId,
          email: response.email,
          username: response.username,
          profilePictureUrl: response.profilePictureUrl,
          profileImageUrl: response.profileImageUrl
        });
        setIsAuthenticated(true);
        
        // Reset verification time to allow immediate verification after this
        lastVerificationTime = 0;
        
        // Update the global state through a custom event
        window.dispatchEvent(new CustomEvent('authStateChange', { 
          detail: { isAuthenticated: true, userId: response.userId }
        }));
      } else {
        throw new Error('Invalid user data received from login');
      }
      
      return response;
    } catch (error) {
      console.error('Login error in context:', error);
      clearAuthData();
      throw error;
    } finally {
      setLoading(false);
    }
  };
  
  // Logout handler
  const logout = async () => {
    setLoading(true);
    try {
      await apiLogout();
      setIsAuthenticated(false);
      setUser(null);
    } catch (error) {
      console.error('Logout failed:', error);
      // Still clear local state even if server logout fails
      setIsAuthenticated(false);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };
  
  // Sign in with Google
  const signInWithGoogle = () => {
    apiSignInWithGoogle();
  };
  
  // Cross-tab logout handler
  useEffect(() => {
    const unsubscribe = authChannel.listenForLogout(() => {
      console.log('Received logout event from another tab');
      setUser(null);
      setIsAuthenticated(false);
    });
    
    return () => {
      unsubscribe();
    };
  }, []);
  
  // Cross-tab login handler
  useEffect(() => {
    const unsubscribe = authChannel.listenForLogin(() => {
      console.log('Received login event from another tab');
      loadUserData();
    });
    
    return () => {
      unsubscribe();
    };
  }, [loadUserData]);
  
  return (
    <AuthContext.Provider 
      value={{ 
        isAuthenticated, 
        user, 
        loading,
        login,
        logout,
        signInWithGoogle
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
